import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Link
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Timeline,
  Assessment,
  ExpandMore as ExpandMoreIcon,
  Store as StoreIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

// Memoized table row component to prevent unnecessary re-renders
const TerminalRow = React.memo(({ terminal, onViewDetails, onViewTransactions, onToggleActive, isAdmin }) => {
  console.log('TerminalRow render:', { terminal: terminal.TerminalID, isAdmin, isActive: terminal.isActive });
  
  const isActive = terminal.isActive !== false;
  
  return (
    <TableRow hover style={{ opacity: isActive ? 1 : 0.6 }}>
      <TableCell sx={{ pl: 4 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {isAdmin && (
            <Switch
              checked={isActive}
              onChange={(e) => {
                console.log('Toggle clicked:', terminal.TerminalID, 'new value:', e.target.checked);
                onToggleActive(terminal.TerminalID, e.target.checked);
              }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-switchBase': {
                  color: isActive ? '#4caf50' : '#f44336',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)',
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isActive ? '#81c784' : '#ef5350',
                },
              }}
            />
          )}
          <Chip 
            label={terminal.TerminalID} 
            size="small" 
            variant="outlined"
            color={isActive ? "success" : "error"}
            sx={{
              borderColor: isActive ? '#4caf50' : '#f44336',
              color: isActive ? '#4caf50' : '#f44336',
            }}
          />
          {!isActive && (
            <Chip 
              label="Inactive" 
              size="small" 
              color="error"
              variant="filled"
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => onViewTransactions(terminal.TerminalID, terminal)}
          >
            Transactions
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onViewDetails(terminal.TerminalID, terminal)}
          >
            Details
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// Component for store group (inner accordion)
const StoreGroup = React.memo(({
  storeName,
  terminals,
  expanded,
  onToggle,
  onViewDetails,
  onViewTransactions,
  onToggleActive,
  onViewLocationDetails,
  isAdmin
}) => {
  const terminalCount = terminals.length;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{
        mb: 0.5,
        ml: 2,
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          '&:hover': { backgroundColor: 'action.hover' },
          minHeight: 40,
          '& .MuiAccordionSummary-content': { margin: '8px 0' }
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <StoreIcon color="action" fontSize="small" />
          <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1 }}>
            {storeName}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onViewLocationDetails(storeName);
            }}
            sx={{ mr: 1 }}
          >
            View Details
          </Button>
          <Chip label={terminalCount} size="small" color="default" />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ pl: 4 }}><strong>Terminal ID</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {terminals.map((terminal) => (
                <TerminalRow
                  key={terminal.TerminalID}
                  terminal={terminal}
                  onViewDetails={onViewDetails}
                  onViewTransactions={onViewTransactions}
                  onToggleActive={onToggleActive}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
});

// Component for market group (outer accordion)
const MarketGroup = React.memo(({
  marketName,
  stores,
  expanded,
  onToggle,
  expandedStores,
  onToggleStore,
  onViewDetails,
  onViewTransactions,
  onToggleActive,
  onViewLocationDetails,
  isAdmin
}) => {
  const totalTerminals = stores.reduce((sum, [_, terminals]) => sum + terminals.length, 0);
  const storeCount = stores.length;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{ mb: 1, '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'primary.light',
          color: 'primary.contrastText',
          '&:hover': { backgroundColor: 'primary.main' }
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
            {marketName}
          </Typography>
          <Chip
            label={`${storeCount} store${storeCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{ bgcolor: 'white', color: 'primary.main' }}
          />
          <Badge badgeContent={totalTerminals} color="secondary" max={999}>
            <Chip label="Terminals" size="small" sx={{ bgcolor: 'white', color: 'primary.main' }} />
          </Badge>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, backgroundColor: 'grey.100' }}>
        {stores.map(([storeName, storeTerminals]) => (
          <StoreGroup
            key={storeName}
            storeName={storeName}
            terminals={storeTerminals}
            expanded={expandedStores[`${marketName}|${storeName}`] || false}
            onToggle={onToggleStore(marketName, storeName)}
            onViewDetails={onViewDetails}
            onViewTransactions={onViewTransactions}
            onToggleActive={onToggleActive}
            onViewLocationDetails={onViewLocationDetails}
            isAdmin={isAdmin}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
});

// Legacy component for location group (for terminals without market)
const LocationGroup = React.memo(({
  locationName,
  terminals,
  expanded,
  onToggle,
  onViewDetails,
  onViewTransactions,
  onToggleActive,
  onViewLocationDetails,
  isAdmin
}) => {
  const terminalCount = terminals.length;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{ mb: 1, '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'background.paper',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <StoreIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium" sx={{ flexGrow: 1 }}>
            {locationName}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onViewLocationDetails(locationName);
            }}
            sx={{ mr: 1 }}
          >
            View Details
          </Button>
          <Badge badgeContent={terminalCount} color="primary" max={999}>
            <Chip label="Terminals" size="small" />
          </Badge>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ pl: 4 }}><strong>Terminal ID</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {terminals.map((terminal) => (
                <TerminalRow
                  key={terminal.TerminalID}
                  terminal={terminal}
                  onViewDetails={onViewDetails}
                  onViewTransactions={onViewTransactions}
                  onToggleActive={onToggleActive}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
});

const Terminals = () => {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedPanels, setExpandedPanels] = useState({});   // For markets
  const [expandedStores, setExpandedStores] = useState({});   // For stores within markets
  const [allExpanded, setAllExpanded] = useState(false);
  const searchTimeoutRef = useRef(null);
  const currentLoadRef = useRef(null); // Track current load to prevent race conditions
  const stateRestoredRef = useRef(false); // Track if state was restored from navigation
  const restoredStoreRef = useRef(null); // Track store name to load terminals for after restore
  const navigate = useNavigate();
  const location = useLocation();
  const { getTerminals, apiCall, getTerminalsSummary, getTerminalsByStore } = useApi();
  const { user, userProfile, loading: authLoading } = useAuth();
  // Admin-only controls (e.g. the Active/Inactive toggle). The app role lives on
  // userProfile (from the Cognito custom:role claim) — NOT on `user` (the raw Cognito
  // object). The backend independently enforces admin-only on PUT /api/v1/terminals/{id}
  // (403 otherwise); this just hides the control from non-admins in the UI.
  const isAdmin = userProfile?.role === 'admin';

  // Summary view state (fast mode)
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [storeTerminals, setStoreTerminals] = useState({}); // Cache terminals by store
  const [backendSearchResults, setBackendSearchResults] = useState(null);
  const [backendSearching, setBackendSearching] = useState(false);
  const [loadingStores, setLoadingStores] = useState({}); // Track loading state per store

  console.log('Terminals component - User:', user, 'User role:', user?.role, 'isAdmin forced to:', isAdmin, 'authLoading:', authLoading);

  // Restore search state from navigation or clear if requested
  useEffect(() => {
    if (location.pathname === '/terminals') {
      if (location.state?.clearSearch) {
        // Clear search when explicitly requested (e.g., from menu)
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setPage(0);
        navigate('/terminals', { replace: true, state: {} });
      } else if (location.state?.preserveSearch) {
        // Restore search state when coming back from transaction details
        const { searchTerm: savedSearch, page: savedPage, expandedPanels: savedPanels, expandedStoreName } = location.state;
        if (savedSearch !== undefined) setSearchTerm(savedSearch);
        if (savedSearch !== undefined) setDebouncedSearchTerm(savedSearch);
        if (savedPage !== undefined) setPage(savedPage);
        if (savedPanels) {
          setExpandedPanels(savedPanels);
          setExpandedStores(savedPanels); // Also restore for summary view
          stateRestoredRef.current = true; // Mark that we restored state
        }
        // Store the expanded store name for loading terminals after summary loads
        if (expandedStoreName) {
          restoredStoreRef.current = expandedStoreName;
        }
        // Clear state to prevent re-restoring on subsequent renders
        navigate('/terminals', { replace: true, state: {} });
      }
    }
  }, [location.pathname, location.state, navigate]);


  // Improved debounce with consistent delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Only set searching state if we're actually going to search
    if (searchTerm !== debouncedSearchTerm && searchTerm.length >= 2) {
      setIsSearching(true);
    }
    
    // Use consistent delay to prevent race conditions
    // Longer delay (500ms) reduces API calls while typing for better responsiveness
    const debounceDelay = searchTerm.length === 0 ? 50 : 500; // Quick clear, longer search delay
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, debounceDelay);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    console.log('Search term changed to:', debouncedSearchTerm);
    if (page !== 0) {
      console.log('Resetting page to 0');
      setPage(0);
    }
  }, [debouncedSearchTerm]);

  // Backend search fallback: when local summary filter finds nothing for a terminal ID pattern
  useEffect(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 2 || !summaryData) {
      setBackendSearchResults(null);
      return;
    }

    // Check if local filter would find results
    const searchLower = debouncedSearchTerm.toLowerCase().trim();
    const hasLocalResults = summaryData.markets?.some(market => {
      if (market.name.toLowerCase().includes(searchLower)) return true;
      if (market.subMarkets) {
        return market.subMarkets.some(sm =>
          sm.name.toLowerCase().includes(searchLower) ||
          sm.stores.some(s => s.name.toLowerCase().includes(searchLower))
        );
      }
      return market.stores?.some(s => s.name.toLowerCase().includes(searchLower));
    });

    if (hasLocalResults) {
      setBackendSearchResults(null);
      return;
    }

    // No local results — use fast backend search
    const doBackendSearch = async () => {
      setBackendSearching(true);
      try {
        // /quick-search route doesn't exist in API Gateway; the list endpoint
        // /api/v1/terminals accepts the same `search` param and returns the
        // same {terminals: [...]} shape from getTerminalsList.
        const data = await apiCall(`/api/v1/terminals?search=${encodeURIComponent(debouncedSearchTerm)}`, 'GET');
        setBackendSearchResults(data?.terminals || []);
      } catch (err) {
        console.error('Backend search failed:', err);
        setBackendSearchResults([]);
      } finally {
        setBackendSearching(false);
      }
    };
    doBackendSearch();
  }, [debouncedSearchTerm, summaryData, apiCall]);

  // Load summary data (fast - ~200ms)
  const loadSummary = useCallback(async () => {
    console.log('Loading terminal summary...');
    setSummaryLoading(true);
    try {
      const data = await getTerminalsSummary();
      if (data) {
        setSummaryData(data);
        console.log(`Summary loaded: ${data.totalMarkets} markets, ${data.totalStores} stores, ${data.totalTerminals} terminals in ${data.queryTimeMs}ms`);
        // Auto-expand first market only if state wasn't restored from navigation
        if (data.markets && data.markets.length > 0 && !stateRestoredRef.current) {
          setExpandedPanels({ [data.markets[0].name]: true });
        }
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
      setError('Failed to load terminal summary');
    } finally {
      setSummaryLoading(false);
      setLoading(false);
    }
  }, [getTerminalsSummary]);

  // Load terminals for a specific store (lazy loading)
  const loadStoreTerminals = useCallback(async (storeName) => {
    if (storeTerminals[storeName]) {
      return; // Already loaded
    }
    setLoadingStores(prev => ({ ...prev, [storeName]: true }));
    try {
      const data = await getTerminalsByStore(storeName);
      if (data && data.terminals) {
        setStoreTerminals(prev => ({ ...prev, [storeName]: data.terminals }));
      }
    } catch (err) {
      console.error(`Failed to load terminals for ${storeName}:`, err);
    } finally {
      setLoadingStores(prev => ({ ...prev, [storeName]: false }));
    }
  }, [getTerminalsByStore, storeTerminals]);

  // Load summary on mount
  useEffect(() => {
    loadSummary();
  }, []);

  // Load terminals for restored store after summary loads
  useEffect(() => {
    if (!summaryLoading && restoredStoreRef.current) {
      const storeName = restoredStoreRef.current;
      restoredStoreRef.current = null; // Clear to prevent re-loading
      // Load terminals for the restored store
      if (!storeTerminals[storeName]) {
        loadStoreTerminals(storeName);
      }
    }
  }, [summaryLoading, storeTerminals, loadStoreTerminals]);

  const loadTerminals = useCallback(async () => {
    const loadId = Math.random().toString(36).substring(7);
    currentLoadRef.current = loadId; // Set this as the current load
    console.log(`[${loadId}] Starting load - search: "${debouncedSearchTerm}", page: ${page}`);
    
    try {
      // Only show loading spinner on initial load, not during search operations
      if (initialLoad) {
        setLoading(true);
      }
      
      // Only send search term if it's at least 2 characters long
      const searchParam = debouncedSearchTerm.length >= 2 ? debouncedSearchTerm.trim() : '';
      
      // Skip API call if search is too short and not initial load
      if (!initialLoad && debouncedSearchTerm.length === 1) {
        console.log(`[${loadId}] Search too short, clearing results`);
        if (currentLoadRef.current === loadId) {
          setTerminals([]);
          setTotalCount(0);
        }
        return;
      }
      
      // Simplified API call - provisioned concurrency eliminates cold start issues
      try {
        console.log(`[${loadId}] API call, search: "${searchParam}"`);
        
        const response = await getTerminals({
          page: page + 1,
          limit: rowsPerPage,
          search: searchParam
        });
        
        // Success! But only update if this is still the current load
        if (response?.terminals && currentLoadRef.current === loadId) {
          setTerminals(response.terminals);
          setTotalCount(response.pagination?.total || 0);
          console.log(`[${loadId}] SUCCESS - loaded ${response.terminals.length} terminals for search: "${searchParam}"`);
          // Debug: Log Market field
          const marketsFound = response.terminals.filter(t => t.Market).length;
          console.log(`[${loadId}] Terminals with Market field: ${marketsFound}/${response.terminals.length}`);
          if (response.terminals.length > 0) {
            console.log('[DEBUG] First 3 terminals:', response.terminals.slice(0, 3).map(t => ({ id: t.TerminalID, loc: t.LocationName, market: t.Market })));
          }
        } else if (currentLoadRef.current !== loadId) {
          console.log(`[${loadId}] IGNORED - superseded by newer search (current: ${currentLoadRef.current})`);
        }
        
      } catch (err) {
        console.error(`API call failed:`, err.message);
        throw err;
      }
      
    } catch (err) {
      setError(err.message || 'Failed to load terminals');
      // Don't clear terminals on error - keep showing previous results
    } finally {
      if (initialLoad) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [page, rowsPerPage, debouncedSearchTerm, initialLoad]);


  const handleViewDetails = useCallback((terminalId, terminal = {}) => {
    // Pass current search state and terminal info so we can restore/expand when coming back
    navigate(`/terminals/${terminalId}`, {
      state: {
        returnSearch: searchTerm,
        returnPage: page,
        returnExpandedPanels: { ...expandedPanels, ...expandedStores },
        terminalMarket: terminal.Market,
        terminalLocation: terminal.LocationName
      }
    });
  }, [navigate, searchTerm, page, expandedPanels, expandedStores]);

  const handleViewTransactions = useCallback((terminalId, terminal = {}) => {
    // Pass current search state and terminal info so we can restore/expand when coming back
    navigate(`/terminals/${terminalId}/transactions`, {
      state: {
        returnSearch: searchTerm,
        returnPage: page,
        returnExpandedPanels: { ...expandedPanels, ...expandedStores },
        terminalMarket: terminal.Market,
        terminalLocation: terminal.LocationName
      }
    });
  }, [navigate, searchTerm, page, expandedPanels, expandedStores]);

  const handleViewLocationDetails = useCallback((locationName) => {
    navigate(`/locations/${encodeURIComponent(locationName)}`, {
      state: {
        returnSearch: searchTerm,
        returnPage: page,
        returnExpandedPanels: { ...expandedPanels, ...expandedStores }
      }
    });
  }, [navigate, searchTerm, page, expandedPanels, expandedStores]);

  // From a terminal-ID search result: filter the tree to that location, open it and load its terminals
  const handleShowLocationTerminals = useCallback((storeName) => {
    const panels = {};
    summaryData?.markets?.forEach(market => {
      if (market.subMarkets) {
        market.subMarkets.forEach(subMarket => {
          if (subMarket.stores.some(s => s.name === storeName)) {
            panels[market.name] = true;
            panels[`${market.name}|${subMarket.name}`] = true;
          }
        });
      } else if (market.stores?.some(s => s.name === storeName)) {
        panels[market.name] = true;
      }
    });
    setBackendSearchResults(null);
    setSearchTerm(storeName);
    setExpandedPanels(prev => ({ ...prev, ...panels }));
    setExpandedStores(prev => ({ ...prev, [storeName]: true }));
    loadStoreTerminals(storeName);
  }, [summaryData, loadStoreTerminals]);

  const handleToggleActive = useCallback(async (terminalId, isActive) => {
    console.log('handleToggleActive called:', { terminalId, isActive, apiCall: typeof apiCall });
    
    if (!apiCall) {
      console.error('apiCall is not defined!');
      return;
    }
    
    try {
      console.log('Calling API to toggle terminal:', terminalId, 'to', isActive);
      const response = await apiCall(`/api/v1/terminals/${terminalId}`, 'PUT', { isActive });
      console.log('Toggle response:', response);
      
      if (response && response.success) {
        // Update both the flat list (search results) and the per-store accordion cache.
        setTerminals(prev =>
          prev.map(t => t.TerminalID === terminalId ? { ...t, isActive } : t)
        );
        setStoreTerminals(prev => {
          const next = {};
          for (const [store, list] of Object.entries(prev)) {
            next[store] = list.map(t => t.TerminalID === terminalId ? { ...t, isActive } : t);
          }
          return next;
        });
        console.log('Terminal state updated locally');
      } else {
        console.error('Toggle failed, response:', response);
      }
    } catch (error) {
      console.error('Error toggling terminal status:', error);
      // Revert the toggle on error in both stores
      setTerminals(prev =>
        prev.map(t => t.TerminalID === terminalId ? { ...t, isActive: !isActive } : t)
      );
      setStoreTerminals(prev => {
        const next = {};
        for (const [store, list] of Object.entries(prev)) {
          next[store] = list.map(t => t.TerminalID === terminalId ? { ...t, isActive: !isActive } : t);
        }
        return next;
      });
    }
  }, [apiCall]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  const formatNumber = useCallback((num) => {
    return new Intl.NumberFormat('en-US').format(num);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Handle market accordion toggle
  const handleMarketToggle = useCallback((marketName) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [marketName]: isExpanded
    }));
  }, []);

  // Handle store accordion toggle
  const handleStoreToggle = useCallback((marketName, storeName) => (event, isExpanded) => {
    setExpandedStores(prev => ({
      ...prev,
      [`${marketName}|${storeName}`]: isExpanded
    }));
  }, []);

  // Legacy handler for locations without market
  const handleAccordionToggle = useCallback((locationName) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [locationName]: isExpanded
    }));
  }, []);

  // Toggle all accordions
  const handleToggleAll = useCallback(() => {
    setAllExpanded(prev => {
      const newState = !prev;
      const newExpandedPanels = {};
      const newExpandedStores = {};

      // Expand/collapse all markets and stores
      terminals.forEach(terminal => {
        const market = terminal.Market || 'Other';
        const store = terminal.LocationName || 'Unknown Location';
        newExpandedPanels[market] = newState;
        newExpandedStores[`${market}|${store}`] = newState;
      });

      setExpandedPanels(newExpandedPanels);
      setExpandedStores(newExpandedStores);
      return newState;
    });
  }, [terminals]);

  // Group terminals by Market -> Store
  const { marketGroups, ungroupedTerminals } = useMemo(() => {
    const markets = {};
    const ungrouped = {};

    terminals.forEach(terminal => {
      const market = terminal.Market;
      const store = terminal.LocationName || 'Unknown Location';

      if (market) {
        // Terminal has a market - group under market -> store
        if (!markets[market]) {
          markets[market] = {};
        }
        if (!markets[market][store]) {
          markets[market][store] = [];
        }
        markets[market][store].push(terminal);
      } else {
        // No market - group by location only (legacy behavior)
        if (!ungrouped[store]) {
          ungrouped[store] = [];
        }
        ungrouped[store].push(terminal);
      }
    });

    // Convert markets to sorted array of [market, [[store, terminals], ...]]
    const marketGroups = Object.entries(markets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([market, stores]) => [
        market,
        Object.entries(stores).sort(([a], [b]) => a.localeCompare(b))
      ]);

    // Convert ungrouped to sorted array
    const ungroupedTerminals = Object.entries(ungrouped)
      .sort(([a], [b]) => {
        if (a === 'Unknown Location') return 1;
        if (b === 'Unknown Location') return -1;
        return a.localeCompare(b);
      });

    return { marketGroups, ungroupedTerminals };
  }, [terminals]);

  // For backward compatibility - count of all groups
  const groupedTerminals = useMemo(() => {
    return [...marketGroups.map(([m, stores]) => [m, stores.flat()]), ...ungroupedTerminals];
  }, [marketGroups, ungroupedTerminals]);

  if (loading && initialLoad) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Terminals
        </Typography>
        <Box display="flex" gap={1}>
          <Chip
            label="Refresh"
            onClick={() => loadSummary()}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Terminal Summary */}
      <>
          {/* Search Controls for Summary View */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Search Terminals"
                    placeholder="Search by Terminal ID or Location Name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant="body2" color="text.secondary">
                    {summaryData ? `${summaryData.totalMarkets} channels, ${summaryData.totalStores} stores, ${summaryData.totalTerminals} terminals` : 'Loading...'}
                    {searchTerm && ` (filtering by "${searchTerm}")`}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {summaryLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : summaryData ? (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {summaryData.totalMarkets} channels, {summaryData.totalStores} stores, {summaryData.totalTerminals} terminals
                  {summaryData.queryTimeMs && ` (loaded in ${summaryData.queryTimeMs}ms)`}
                </Typography>

                {/* Filter markets/stores based on search term */}
                {(() => {
                  const searchLower = searchTerm.toLowerCase().trim();

                  // Helper function to check if store matches search
                  const storeMatchesSearch = (store, searchLower) => {
                    if (store.name.toLowerCase().includes(searchLower)) return true;
                    if (store.terminals?.some(t => t.terminal_id?.toLowerCase().includes(searchLower))) return true;
                    return false;
                  };

                  // Filter markets (which may have subMarkets for Corp -> State hierarchy)
                  const filteredMarkets = searchLower ? summaryData.markets?.filter(market => {
                    // Check if market/org name matches
                    if (market.name.toLowerCase().includes(searchLower)) return true;

                    // Check subMarkets (for Corp -> State -> Store hierarchy like Rise)
                    if (market.subMarkets) {
                      return market.subMarkets.some(subMarket => {
                        if (subMarket.name.toLowerCase().includes(searchLower)) return true;
                        return subMarket.stores.some(store => storeMatchesSearch(store, searchLower));
                      });
                    }

                    // Check stores directly (for single-market orgs)
                    if (market.stores) {
                      return market.stores.some(store => storeMatchesSearch(store, searchLower));
                    }

                    return false;
                  }).map(market => {
                    // Filter subMarkets if they exist (Corp -> State -> Store)
                    if (market.subMarkets) {
                      return {
                        ...market,
                        subMarkets: market.subMarkets.filter(subMarket => {
                          if (market.name.toLowerCase().includes(searchLower)) return true;
                          if (subMarket.name.toLowerCase().includes(searchLower)) return true;
                          return subMarket.stores.some(store => storeMatchesSearch(store, searchLower));
                        }).map(subMarket => ({
                          ...subMarket,
                          stores: subMarket.stores.filter(store => {
                            if (market.name.toLowerCase().includes(searchLower)) return true;
                            if (subMarket.name.toLowerCase().includes(searchLower)) return true;
                            return storeMatchesSearch(store, searchLower);
                          })
                        }))
                      };
                    }

                    // Filter stores directly (single-market orgs)
                    return {
                      ...market,
                      stores: market.stores?.filter(store => {
                        if (market.name.toLowerCase().includes(searchLower)) return true;
                        return storeMatchesSearch(store, searchLower);
                      })
                    };
                  }) : summaryData.markets;

                  // Helper component to render a store accordion
                  const renderStoreAccordion = (store, parentKey = '', marketName = '') => (
                    <Accordion
                      key={`${parentKey}${store.name}`}
                      expanded={expandedStores[store.name] || false}
                      onChange={(e, expanded) => {
                        setExpandedStores(prev => ({ ...prev, [store.name]: expanded }));
                        if (expanded) loadStoreTerminals(store.name);
                      }}
                      sx={{ mb: 0.5 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" pr={2}>
                          <Typography>{store.name}</Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewLocationDetails(store.name);
                              }}
                            >
                              Location Details
                            </Button>
                            <Badge badgeContent={store.terminalCount} color="primary" max={999}>
                              <Typography variant="caption" color="text.secondary">terminals</Typography>
                            </Badge>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {loadingStores[store.name] ? (
                          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
                        ) : storeTerminals[store.name] ? (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Terminal ID</strong></TableCell>
                                  {isAdmin && <TableCell align="center"><strong>Status</strong></TableCell>}
                                  <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {storeTerminals[store.name].map(t => {
                                  const tActive = t.isActive !== false;
                                  return (
                                  <TableRow key={t.TerminalID} hover style={{ opacity: tActive ? 1 : 0.6 }}>
                                    <TableCell>
                                      {t.TerminalID}
                                      {!tActive && (
                                        <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                      )}
                                    </TableCell>
                                    {isAdmin && (
                                      <TableCell align="center">
                                        <Switch
                                          checked={tActive}
                                          size="small"
                                          color="success"
                                          onChange={(e) => handleToggleActive(t.TerminalID, e.target.checked)}
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell align="center">
                                      <Box display="flex" gap={1} justifyContent="center">
                                        <Button variant="outlined" size="small" onClick={() => handleViewTransactions(t.TerminalID, { Market: marketName, LocationName: store.name })}>
                                          Transactions
                                        </Button>
                                        <Button variant="outlined" size="small" onClick={() => handleViewDetails(t.TerminalID, { Market: marketName, LocationName: store.name })}>
                                          Details
                                        </Button>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Click to load terminals</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );

                  return filteredMarkets?.map(market => (
                  <Accordion
                    key={market.name}
                    expanded={expandedPanels[market.name] || false}
                    onChange={(e, expanded) => setExpandedPanels(prev => ({ ...prev, [market.name]: expanded }))}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ backgroundColor: 'primary.main', color: 'white', '& .MuiAccordionSummary-expandIconWrapper': { color: 'white' } }}
                    >
                      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" pr={2}>
                        <Typography variant="subtitle1" fontWeight="bold">{market.name}</Typography>
                        <Box display="flex" gap={1}>
                          {/* Show subMarkets count for Corp -> State hierarchy, or stores count for single-market */}
                          {market.subMarkets ? (
                            <Chip label={`${market.subMarkets.length} regions`} size="small" sx={{ bgcolor: 'white', color: 'primary.main' }} />
                          ) : (
                            <Chip label={`${market.stores?.length || 0} stores`} size="small" sx={{ bgcolor: 'white', color: 'primary.main' }} />
                          )}
                          <Badge badgeContent={market.totalTerminals} color="error" max={9999}>
                            <Chip label="Terminals" size="small" sx={{ bgcolor: 'white', color: 'primary.main' }} />
                          </Badge>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {/* Handle Corp -> State -> Store hierarchy (subMarkets) */}
                      {market.subMarkets ? (
                        market.subMarkets.map(subMarket => (
                          <Accordion
                            key={`${market.name}-${subMarket.name}`}
                            expanded={expandedPanels[`${market.name}|${subMarket.name}`] || false}
                            onChange={(e, expanded) => setExpandedPanels(prev => ({ ...prev, [`${market.name}|${subMarket.name}`]: expanded }))}
                            sx={{ mb: 0.5, ml: 1 }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ backgroundColor: 'grey.200' }}
                            >
                              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" pr={2}>
                                <Typography variant="body1" fontWeight="medium">{subMarket.name}</Typography>
                                <Box display="flex" gap={1}>
                                  <Chip label={`${subMarket.stores.length} stores`} size="small" variant="outlined" />
                                  <Badge badgeContent={subMarket.totalTerminals} color="primary" max={999}>
                                    <Typography variant="caption" color="text.secondary">terminals</Typography>
                                  </Badge>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pl: 2 }}>
                              {subMarket.stores.map(store => renderStoreAccordion(store, `${market.name}-${subMarket.name}-`, market.name))}
                            </AccordionDetails>
                          </Accordion>
                        ))
                      ) : (
                        /* Handle single-market orgs with stores directly */
                        market.stores?.map(store => renderStoreAccordion(store, `${market.name}-`, market.name))
                      )}
                    </AccordionDetails>
                  </Accordion>
                ));
                })()}
              </CardContent>
            </Card>
          ) : null}

          {/* Backend search fallback results */}
          {backendSearching && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Searching all data sources...</Typography>
            </Box>
          )}
          {backendSearchResults && backendSearchResults.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Found {backendSearchResults.length} terminal{backendSearchResults.length !== 1 ? 's' : ''} from transaction data:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Terminal ID</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Market</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backendSearchResults.map((t) => (
                      <TableRow key={t.TerminalID} hover sx={{ cursor: 'pointer' }}>
                        <TableCell>{t.TerminalID}</TableCell>
                        <TableCell>
                          {t.LocationName && t.LocationName !== 'Unknown' ? (
                            <Tooltip title="Show all terminals at this location">
                              <Link component="button" variant="body2" onClick={() => handleShowLocationTerminals(t.LocationName)}>
                                {t.LocationName}
                              </Link>
                            </Tooltip>
                          ) : 'Unknown'}
                        </TableCell>
                        <TableCell>{t.Market || '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleViewDetails(t.TerminalID, t)}>View</Button>
                          <Button size="small" onClick={() => handleViewTransactions(t.TerminalID, t)}>Transactions</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {backendSearchResults && backendSearchResults.length === 0 && !backendSearching && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No terminals found matching "{searchTerm}" in any data source.
            </Typography>
          )}
        </>

    </Box>
  );
};

export default Terminals;